import React from "react";
import styled from "styled-components";
import Button from "../../../Generic/Button";
import Dropdown from "../../../Generic/Dropdown";
import {
	ACTION_TARGET_OPTIONS,
	ACTION_TYPE_OPTIONS,
	CONDITION_OPERATOR_OPTIONS,
	CONDITION_OPERATOR_OPTIONS_BY_SUBJECT,
	CONDITION_SUBJECT_OPTIONS,
	ConditionOperator,
	ConditionSubject,
	OfferActionField,
	OfferConditionField,
	OfferErrorKey,
	OfferField,
	OfferRuleField,
	RULE_OPERATOR_OPTIONS,
} from "../../constants";
import { createEmptyCondition } from "../../utils/offerDraft";
import DishSearch from "../DishSearch";
import {
	Section,
	SectionTitle,
	FieldRow,
	Field,
	FullWidthField,
	FieldLabel,
	FieldInput,
	FieldTextarea,
	DropdownWrap,
	ErrorText,
} from "../shared/OfferFormLayout";

const ConditionCard = styled.div`
	border: 1px solid rgba(0, 0, 0, 0.1);
	border-radius: 10px;
	padding: 1rem;
	display: flex;
	flex-direction: column;
	gap: 1rem;
`;

const ConditionHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
`;

const RulesAndActionStep = ({ draft, errors, onChange }) => {
	const rule = draft[OfferField.RULE] || {};
	const conditions = rule[OfferRuleField.CONDITIONS] || [];
	const action = rule[OfferRuleField.ACTION] || {};

	const updateRule = (updates, clearErrorKeys = []) =>
		onChange({ [OfferField.RULE]: { ...rule, ...updates } }, clearErrorKeys);

	const updateAction = (field, value, errorKey) =>
		onChange(
			{
				[OfferField.RULE]: {
					...rule,
					[OfferRuleField.ACTION]: { ...action, [field]: value },
				},
			},
			errorKey ? [errorKey] : []
		);

	const updateCondition = (index, field, value) => {
		const next = conditions.map((c, i) =>
			i === index ? { ...c, [field]: value } : c
		);
		updateRule({ [OfferRuleField.CONDITIONS]: next }, [OfferErrorKey.CONDITIONS]);
	};

	const updateConditionFields = (index, fields) => {
		const next = conditions.map((c, i) => (i === index ? { ...c, ...fields } : c));
		updateRule({ [OfferRuleField.CONDITIONS]: next }, [OfferErrorKey.CONDITIONS]);
	};

	const handleConditionSubjectChange = (index, nextSubject) => {
		const currentSubject = conditions[index]?.[OfferConditionField.SUBJECT];
		if (nextSubject === ConditionSubject.DISH_ID) {
			updateConditionFields(index, {
				[OfferConditionField.SUBJECT]: nextSubject,
				[OfferConditionField.OPERATOR]: ConditionOperator.EQUALS,
				[OfferConditionField.VALUE]: "",
				[OfferConditionField.VALUE_LABEL]: "",
			});
			return;
		}
		if (currentSubject === ConditionSubject.DISH_ID) {
			updateConditionFields(index, {
				[OfferConditionField.SUBJECT]: nextSubject,
				[OfferConditionField.VALUE]: "",
				[OfferConditionField.VALUE_LABEL]: "",
			});
			return;
		}
		updateCondition(index, OfferConditionField.SUBJECT, nextSubject);
	};

	const handleSelectDish = (index, selection) => {
		if (!selection?.value) {
			return;
		}
		updateConditionFields(index, {
			[OfferConditionField.VALUE]: selection.value,
			[OfferConditionField.VALUE_LABEL]: selection.valueLabel || selection.value,
		});
	};

	const handleClearDish = (index) => {
		updateConditionFields(index, {
			[OfferConditionField.VALUE]: "",
			[OfferConditionField.VALUE_LABEL]: "",
		});
	};

	const addCondition = () =>
		updateRule(
			{ [OfferRuleField.CONDITIONS]: [...conditions, createEmptyCondition()] },
			[OfferErrorKey.CONDITIONS]
		);

	const removeCondition = (index) => {
		if (conditions.length <= 1) return;
		updateRule(
			{ [OfferRuleField.CONDITIONS]: conditions.filter((_, i) => i !== index) },
			[OfferErrorKey.CONDITIONS]
		);
	};

	return (
		<Section>
			<SectionTitle>Rule Description</SectionTitle>
			<FullWidthField id="offer-rules-description-field">
				<FieldLabel htmlFor="offer-rules-description">Description</FieldLabel>
				<FieldTextarea
					id="offer-rules-description"
					value={rule[OfferRuleField.DESCRIPTION] || ""}
					onChange={(e) =>
						updateRule(
							{ [OfferRuleField.DESCRIPTION]: e.target.value },
							[OfferErrorKey.RULE_DESCRIPTION]
						)
					}
					height="4rem"
				/>
				{errors[OfferErrorKey.RULE_DESCRIPTION] && (
					<ErrorText>{errors[OfferErrorKey.RULE_DESCRIPTION]}</ErrorText>
				)}
			</FullWidthField>

			<SectionTitle>Conditions</SectionTitle>
			<Field id="offer-rules-operator-field">
				<FieldLabel htmlFor="offer-rules-operator">Combine Operator</FieldLabel>
				<DropdownWrap id="offer-rules-operator">
					<Dropdown
						optionsList={RULE_OPERATOR_OPTIONS}
						value={rule[OfferRuleField.OPERATOR]}
						onChange={(v) => updateRule({ [OfferRuleField.OPERATOR]: v })}
					/>
				</DropdownWrap>
			</Field>

			{errors[OfferErrorKey.CONDITIONS] ? (
				<ErrorText>{errors[OfferErrorKey.CONDITIONS]}</ErrorText>
			) : null}

			{conditions.map((condition, index) => {
				const isDishIdSubject =
					condition[OfferConditionField.SUBJECT] === ConditionSubject.DISH_ID;

				return (
					<ConditionCard key={index} id={`offer-rules-condition-${index}`}>
						<ConditionHeader>
							<strong>Condition {index + 1}</strong>
							{conditions.length > 1 && (
								<Button onClick={() => removeCondition(index)}>Remove</Button>
							)}
						</ConditionHeader>
						<FieldRow>
							<Field id={`offer-rules-condition-${index}-subject-field`}>
								<FieldLabel htmlFor={`offer-rules-condition-${index}-subject`}>
									Subject
								</FieldLabel>
								<DropdownWrap id={`offer-rules-condition-${index}-subject`}>
									<Dropdown
										head="Select subject"
										optionsList={CONDITION_SUBJECT_OPTIONS}
										value={condition[OfferConditionField.SUBJECT]}
										onChange={(v) => handleConditionSubjectChange(index, v)}
									/>
								</DropdownWrap>
							</Field>
							<Field id={`offer-rules-condition-${index}-operator-field`}>
								<FieldLabel htmlFor={`offer-rules-condition-${index}-operator`}>
									Operator
								</FieldLabel>
								<DropdownWrap id={`offer-rules-condition-${index}-operator`}>
									<Dropdown
										key={condition[OfferConditionField.SUBJECT] || "subject"}
										head="Select operator"
										optionsList={
											CONDITION_OPERATOR_OPTIONS_BY_SUBJECT[
												condition[OfferConditionField.SUBJECT]
											] || CONDITION_OPERATOR_OPTIONS
										}
										value={condition[OfferConditionField.OPERATOR]}
										onChange={(v) =>
											updateCondition(index, OfferConditionField.OPERATOR, v)
										}
									/>
								</DropdownWrap>
							</Field>
							<Field id={`offer-rules-condition-${index}-value-field`}>
								<FieldLabel htmlFor={`offer-rules-condition-${index}-value`}>
									Value
								</FieldLabel>
								{isDishIdSubject ? (
									<DishSearch
										selectedLabel={
											condition[OfferConditionField.VALUE_LABEL] ||
											condition[OfferConditionField.VALUE] ||
											""
										}
										onSelect={(selection) =>
											handleSelectDish(index, selection)
										}
										onClear={() => handleClearDish(index)}
									/>
								) : (
									<FieldInput
										id={`offer-rules-condition-${index}-value`}
										value={condition[OfferConditionField.VALUE]}
										onChange={(e) =>
											updateCondition(
												index,
												OfferConditionField.VALUE,
												e.target.value
											)
										}
									/>
								)}
							</Field>
						</FieldRow>
					</ConditionCard>
				);
			})}

			<Button onClick={addCondition} active>
				Add Condition
			</Button>

			<SectionTitle>Action</SectionTitle>
			<FieldRow>
				<Field id="offer-rules-action-type-field">
					<FieldLabel htmlFor="offer-rules-action-type">Action Type</FieldLabel>
					<DropdownWrap id="offer-rules-action-type">
						<Dropdown
							head="Select action type"
							optionsList={ACTION_TYPE_OPTIONS}
							value={action[OfferActionField.TYPE]}
							onChange={(v) =>
								updateAction(OfferActionField.TYPE, v, OfferErrorKey.ACTION_TYPE)
							}
						/>
					</DropdownWrap>
					{errors[OfferErrorKey.ACTION_TYPE] ? (
						<ErrorText>{errors[OfferErrorKey.ACTION_TYPE]}</ErrorText>
					) : null}
				</Field>
				<Field id="offer-rules-action-value-field">
					<FieldLabel htmlFor="offer-rules-action-value">Value *</FieldLabel>
					<FieldInput
						id="offer-rules-action-value"
						type="number"
						min="0"
						value={action[OfferActionField.VALUE]}
						onChange={(e) =>
							updateAction(
								OfferActionField.VALUE,
								e.target.value,
								OfferErrorKey.ACTION_VALUE
							)
						}
						errorMessage={errors[OfferErrorKey.ACTION_VALUE]}
						errored={!!errors[OfferErrorKey.ACTION_VALUE]}
					/>
				</Field>
				<Field id="offer-rules-action-target-field">
					<FieldLabel htmlFor="offer-rules-action-target">Target *</FieldLabel>
					<DropdownWrap id="offer-rules-action-target">
						<Dropdown
							head="Select target"
							optionsList={ACTION_TARGET_OPTIONS}
							value={action[OfferActionField.TARGET]}
							onChange={(v) =>
								updateAction(OfferActionField.TARGET, v, OfferErrorKey.ACTION_TARGET)
							}
						/>
					</DropdownWrap>
					{errors[OfferErrorKey.ACTION_TARGET] && (
						<ErrorText>{errors[OfferErrorKey.ACTION_TARGET]}</ErrorText>
					)}
				</Field>
			</FieldRow>

			<FieldRow>
				<Field id="offer-rules-min-amount-field">
					<FieldLabel htmlFor="offer-rules-min-amount">Min Amount Limit</FieldLabel>
					<FieldInput
						id="offer-rules-min-amount"
						type="number"
						min="0"
						value={action[OfferActionField.MIN_AMOUNT_LIMIT]}
						onChange={(e) =>
							updateAction(OfferActionField.MIN_AMOUNT_LIMIT, e.target.value)
						}
					/>
				</Field>
				<Field id="offer-rules-max-amount-field">
					<FieldLabel htmlFor="offer-rules-max-amount">Max Amount Limit</FieldLabel>
					<FieldInput
						id="offer-rules-max-amount"
						type="number"
						min="0"
						value={action[OfferActionField.MAX_AMOUNT_LIMIT]}
						onChange={(e) =>
							updateAction(OfferActionField.MAX_AMOUNT_LIMIT, e.target.value)
						}
					/>
				</Field>
			</FieldRow>
		</Section>
	);
};

export default RulesAndActionStep;
